import React, { FC, ReactNode, createContext, useContext } from 'react';
import { useAuthenticator, Authenticator } from '@aws-amplify/ui-react';
import {
    useAuthenticatorInitMachine,
    useSetUserAgent,
} from '@aws-amplify/ui-react-core';
import { useGet } from '../hooks/useApi';
import { userApi } from '../APIs/userApi';
import { mutate } from 'swr';
import { authenticatorSettings } from '../components/login/authenticatorSettings';

export interface User {
    tenantId: string;
    userId: string;
    email: string;
    /** @deprecated use firstName */
    givenName: string;
    firstName: string;
    /** @deprecated use lastName */
    familyName: string;
    lastName: string;
    role: string;
    phoneNumber: string;
}

type AuthContextType = {
    isLoading: boolean;
    isAuthenticated: boolean;
    user?: User | null;
    isAdmin: () => boolean;
    isUser: () => boolean;
    isSuperAdmin: () => boolean;
    isExternalUser: () => boolean;
    signOut: () => void;
};

const AuthContext = createContext<AuthContextType>({
    isLoading: false,
    isAuthenticated: false,
    user: null,
    isAdmin: () => false,
    isUser: () => false,
    isSuperAdmin: () => false,
    isExternalUser: () => false,
    signOut: () => {},
});

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => (
    <Authenticator.Provider>
        <InternalAuthProvider>{children}</InternalAuthProvider>
    </Authenticator.Provider>
);

export const InternalAuthProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { authStatus, user, signOut: cognitoLogout } = useAuthenticator();

    useSetUserAgent({
        componentName: 'Authenticator',
        packageName: 'react',
        version: '6.15.3',
    });
    useAuthenticatorInitMachine(authenticatorSettings);

    const attributes = useGet(
        user?.userId ? userApi.get.userAttributes(user.userId) : null
    );

    const role = attributes.data?.role;

    const isAdmin = () => role === 'Admin';
    const isUser = () => role === 'User';
    const isSuperAdmin = () => role === 'SuperAdmin';
    const isExternalUser = () => role === 'External';

    const signOut = () => {
        setTimeout(() => {
            cognitoLogout();
            mutate(
                (key) => {
                    if (typeof key !== 'string') return true;
                    return !key.startsWith('/public/');
                },
                undefined,
                { revalidate: false }
            );
        }, 0);
    };

    if (attributes.error) {
        throw attributes.error;
    }

    return (
        <AuthContext.Provider
            value={{
                isLoading: attributes.isLoading || authStatus === 'configuring',
                isAuthenticated: authStatus === 'authenticated',
                user: attributes.data,
                isAdmin,
                isUser,
                isSuperAdmin,
                isExternalUser,
                signOut,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
